<script setup lang="ts">
import {computed} from "vue";
import InputDecorator, {InputDecoratorProps} from './InputDecorator.vue';

export type RadioOption = {
    label: string;
    value: string | number;
    disabled?: boolean;
}

const props = defineProps<{
    id: string,
    name: string,
    value?: number | string,
    disabled?: boolean,
    options: RadioOption[],
} & InputDecoratorProps>();

const emit = defineEmits(['update', 'blur']);

const selectedOption = computed({
    get() {
        return props.value;
    },
    set(value) {
        emit('update', value);
    }
});

const getOptionId = (value: string | number) => props.id + '-' + value;

</script>
<template>
    <InputDecorator :for-id="id" :label="label" :help="help" :help-position="helpPosition" :errors="errors" :required="required">
        <div class="radio-option-container" :id="`radio-container-` + id">
            <div :class="[`radio-option`, {disabled: option.disabled || disabled}]"
                 v-for="(option) in options"
                 :key="option.value"
            >
                <input
                    class="radio-input"
                    type="radio"
                    role="radio"
                    :id="getOptionId(option.value)" :name="name" :value="option.value" v-model="selectedOption"
                    :disabled="option.disabled || disabled"
                    @blur="emit('blur')"
                />
                <label class="radio-label" :for="getOptionId(option.value)">{{ option.label }}</label>
            </div>
        </div>
    </InputDecorator>
</template>
<style lang="scss" scoped>
@import '@Common/Styles/variables';

.radio-option-container {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 5px;

    .radio-option {
        display: flex;
        align-items: center;
        gap: 7px;

        .radio-input {
            appearance: none;
            width: 15px;
            height: 15px;
            border-radius: 50%;
            background: white;
            border: 1px solid var(--gray-400, map-get($yapla-gray, 400));
            transition: border ease 0.15s;

            &:checked {
                border: 5px solid var(--primary, map-get($yapla-orange, 500));
            }
        }

        .radio-label {
            color: var(--gray-600, map-get($yapla-gray, 600));
            margin-bottom: 0;
            cursor: pointer;
        }

        &.disabled {
            .radio-label {
                color: var(--gray-500, map-get($yapla-gray, 500));
            }

            .radio-input {
                &:checked {
                    border: 5px solid var(--gray-400, map-get($yapla-gray, 400));
                }
            }
        }
    }

}
</style>